import { FALLBACK_IMAGE_PATH } from '@susu/custom-made/utils/constants';
import { imageDefinition } from '@susu/custom-made/utils/imageDefinition';
import { imageUrlBuilder } from '@susu/custom-made/utils/imageUrlProvider/imageUrlProvider';
import type { ProductType } from '@susu/custom-made/utils/productDefinition';
import { Products, productCodeByConfigurationId } from '@susu/custom-made/utils/productDefinition';

const DEFAULT_IMAGE_NAME = 'Jacket';

const shimmer =
  "relative overflow-hidden after:content-[''] after:absolute after:top-0 after:left-0 after:w-[15px] after:h-full after:bg-white after:blur-[15px] after:animate-shimmer";

const ICON_TAP_TARGET = 'flex h-44 w-44 items-center justify-center';

function getDefaultImageName(product?: ProductType): string {
  if (!product || !Products[product]) {
    return DEFAULT_IMAGE_NAME;
  }
  const firstConfigurationType = Products[product][0];
  return productCodeByConfigurationId[+firstConfigurationType].replace('Tuxedo', '') ?? DEFAULT_IMAGE_NAME;
}

function getFallbackImageSrc(product: ProductType | undefined, cdnDomain: string): string {
  const imageName = getDefaultImageName(product);

  return imageUrlBuilder({
    imageUrls: [`${FALLBACK_IMAGE_PATH}/${imageName}`],
    imageDefinition: imageDefinition.layer,
    cdnDomain,
  }).src;
}

interface SkeletonProps {
  product?: ProductType;
  /**
   * Host for the placeholder image. Comes from whoever knows the environment —
   * the API payload, the environment context, or a server component — because a
   * client component reading it from the build would freeze one artifact to one
   * environment. The China projects are the only ones that override it.
   */
  cdnDomain?: string;
}

function Card() {
  return (
    <div className="flex flex-col gap-14 md:gap-[17px] lg:w-full lg:gap-14">
      <div
        className={`bg-gray-300 w-[88px] h-[88px] rounded-md ${shimmer} md:w-[116px] md:h-[116px] md:after:w-30 md:after:blur-[25px] lg:w-full lg:h-auto lg:aspect-[169.33/138.9]`}
      ></div>
      <div className="lg:flex lg:flex-col lg:gap-14">
        <div
          className={`w-[58px] h-[11px] bg-gray-300 rounded-sm ${shimmer} md:w-64 md:h-14 md:after:w-30 md:after:blur-[25px] lg:w-[88px] lg:h-12`}
        ></div>
        <div
          className={`hidden bg-gray-300 ${shimmer} lg:block lg:w-[118px] lg:h-12 lg:rounded-sm lg:after:w-30 lg:after:blur-[25px]`}
        ></div>
      </div>
    </div>
  );
}

function Navigation() {
  return (
    <div className="lg:h-[82px] lg:shrink-0 2xl:h-[88px]">
      <div className="flex justify-between items-center lg:px-20 lg:gap-20">
        <div className="flex flex-1 justify-center items-center pt-12 pb-12 pl-16 pr-16 gap-[21px] md:pt-16 md:pb-16 md:pl-120 md:pr-120 md:gap-32 lg:justify-start lg:pt-22 lg:px-0 lg:pb-[13px] lg:gap-22 2xl:pt-20 2xl:pb-[15px] 2xl:gap-32">
          <div
            className={`rounded-[80px] w-72 h-32 bg-gray-200 ${shimmer} md:w-[83px] md:h-[42px] md:after:w-30 md:after:blur-[25px] lg:w-[69px] lg:h-38 2xl:w-[83px] 2xl:h-[42px]`}
          ></div>
          <div
            className={`h-14 w-56 rounded-sm bg-gray-200 ${shimmer} md:h-[18px] md:w-[68px] md:after:w-30 md:after:blur-[25px] lg:h-16 lg:w-56 2xl:h-[18px] 2xl:w-[68px]`}
          ></div>
          <div
            className={`h-14 w-56 rounded-sm bg-gray-200 ${shimmer} md:h-[18px] md:w-[68px] md:after:w-30 md:after:blur-[25px] lg:h-16 lg:w-56 2xl:h-[18px] 2xl:w-[68px]`}
          ></div>
          <div
            className={`h-14 w-56 rounded-sm bg-gray-200 ${shimmer} md:h-[18px] md:w-[68px] md:after:w-30 md:after:blur-[25px] lg:h-16 lg:w-56 2xl:h-[18px] 2xl:w-[68px]`}
          ></div>
        </div>
        {/* Desktop-only Finish button, matching `Button` Primary/Large (`md:h-[42px]`, `md:rounded-[23px]`). */}
        <div className="hidden lg:block">
          <div
            className={`h-[42px] w-[79px] shrink-0 rounded-[23px] bg-gray-200 ${shimmer} after:w-30 after:blur-[25px]`}
          ></div>
        </div>
      </div>
    </div>
  );
}

function SearchHeader() {
  return (
    <div className="hidden lg:grid lg:shrink-0 lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-12 lg:px-10 lg:pb-8">
      <div className={ICON_TAP_TARGET}>
        <div className={`w-20 h-20 rounded-full bg-gray-200 ${shimmer}`}></div>
      </div>
      <div
        className={`w-[74px] h-12 justify-self-center rounded-sm bg-gray-200 ${shimmer} after:w-30 after:blur-[25px]`}
      ></div>
      <div className={`${ICON_TAP_TARGET} justify-self-end`}>
        <div className={`w-20 h-20 rounded-full bg-gray-200 ${shimmer}`}></div>
      </div>
    </div>
  );
}

function Skeleton({ product, cdnDomain = 'cdn.suitsupply.com' }: Readonly<SkeletonProps>) {
  const fallbackSrc = getFallbackImageSrc(product, cdnDomain);

  return (
    <div className="grid h-[calc(100dvh-var(--webstore-header-height))] w-dvw grid-cols-[1fr] grid-rows-[1fr_auto] [grid-template-areas:'preview'_'section'] lg:flex">
      <div className="bg-gray-300 [grid-area:preview] relative overflow-hidden lg:w-[60%]">
        <div className="max-h-[calc(100%-40px)] max-w-full object-contain absolute top-1/2 right-0 bottom-0 left-0 flex aspect-4/5 mt-2 mx-auto overflow-hidden -translate-y-1/2">
          <img src={fallbackSrc} alt="" className="block h-full w-full object-contain" />
        </div>
        <div className="absolute top-0 left-0 flex flex-col gap-6 pt-12 pr-12 pb-12 pl-14 md:pt-20 md:pr-20 md:pb-20 md:pl-20 lg:pl-24">
          <div
            className={`w-80 h-14 rounded-sm bg-gray-200 ${shimmer} md:w-[100px] md:h-16 lg:w-[90px] lg:h-14 2xl:w-[100px] 2xl:h-16`}
          ></div>
          <div
            className={`w-[100px] h-12 rounded-sm bg-gray-200 ${shimmer} md:w-120 md:h-14 lg:w-[110px] lg:h-12 2xl:w-120 2xl:h-14`}
          ></div>
        </div>
        <div className="absolute top-0 right-0 pt-8 pr-12 pb-8 pl-14 md:pt-20 md:pr-20 md:pb-20 md:pl-20 lg:hidden">
          <div
            className={`w-[66px] h-36 my-4 rounded-[21px] bg-gray-200 ${shimmer} md:my-0 md:h-[42px] md:rounded-[23px]`}
          ></div>
        </div>
      </div>
      <div className="[grid-area:section] flex flex-col overflow-hidden h-[188px] md:h-[244px] lg:w-[40%] lg:h-full">
        <Navigation />
        <SearchHeader />
        <div className="flex gap-10 pt-0 pr-16 pb-16 pl-16 md:gap-12 md:pt-0 md:pr-20 md:pb-20 md:pl-20 lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-14 lg:gap-y-20 lg:pt-8 lg:pr-20 lg:pb-20 lg:pl-20 2xl:grid-cols-3">
          {Array.from({ length: 12 }, (_, index) => (
            <Card key={index} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default Skeleton;
